import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';

// Component imports  
import { LearnMoreDialogComponent, LearnMore } from '../learn-more-dialog/learn-more-dialog.component';
import { LearnMoreItemComponent } from '../learn-more-item/learn-more-item.component';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner.component';

@Component({
  selector: 'app-learn-more',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    MessageModule,
    SkeletonModule,
    ToastModule,
    LearnMoreItemComponent,
    LoadingSpinnerComponent
  ],
  providers: [DialogService, MessageService],
  templateUrl: './learn-more.component.html',
  styleUrls: ['./learn-more.component.css']
})
export class LearnMoreComponent implements OnInit, OnDestroy {
  // Loading state
  loading = signal(true);
  
  learnMoreItems: LearnMore[] = [];
  private ref: DynamicDialogRef | undefined;
  private timeout?: number;

  constructor(
    private dialogService: DialogService,
    private translateService: TranslateService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadSampleData();
  }

  ngOnDestroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  private loadSampleData(): void {
    if (this.timeout) clearTimeout(this.timeout);
    
    this.loading.set(true);
    this.learnMoreItems = [];
    
    this.timeout = window.setTimeout(() => {
      // Sample data for demonstration
      this.learnMoreItems = [
        {
          id: '1',
          coverImage: 'https://via.placeholder.com/400x250/8B5CF6/FFFFFF?text=Learn+More+1',
          title: 'Blockchain Fundamentals',
          description: 'Comprehensive guide to understanding blockchain technology, covering consensus mechanisms, smart contracts, and decentralized applications.',
          linkUrl: 'https://example.com/blockchain-fundamentals'
        },
        {
          id: '2',
          coverImage: 'https://via.placeholder.com/400x250/06B6D4/FFFFFF?text=Learn+More+2',
          title: 'Web3 Development Best Practices',
          description: 'Essential practices for building secure and scalable Web3 applications, including security audits, gas optimization, and user experience design.',
          linkUrl: 'https://example.com/web3-best-practices'
        },
        {
          id: '3',
          coverImage: 'https://via.placeholder.com/400x250/F59E0B/FFFFFF?text=Learn+More+3',
          title: 'DeFi Protocol Security',
          description: 'Advanced security considerations for decentralized finance protocols, including common vulnerabilities and prevention strategies.',
          linkUrl: 'https://example.com/defi-security'
        }
      ];
      
      this.loading.set(false);
    }, 2000);
  }

  openAddLearnMoreDialog(): void {
    this.ref = this.dialogService.open(LearnMoreDialogComponent, {
      header: this.translateService.instant('learnMore.addTitle'),
      width: '700px',
      modal: true,
      closable: true
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result && result.success) {
        this.addLearnMoreItem(result.data);
        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('learnMore.success.addTitle'),
          detail: this.translateService.instant('learnMore.success.addMessage'),
          life: 4000
        });
      }
    });
  }

  openEditLearnMoreDialog(item: LearnMore, index: number): void {
    this.ref = this.dialogService.open(LearnMoreDialogComponent, {
      header: this.translateService.instant('learnMore.editTitle'),
      width: '700px',
      modal: true,
      closable: true,
      data: { learnMore: item }
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result && result.success) {
        this.updateLearnMoreItem(result.data, index);
        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('learnMore.success.editTitle'),
          detail: this.translateService.instant('learnMore.success.editMessage'),
          life: 4000
        });
      }
    });
  }

  addLearnMoreItem(item: LearnMore): void {
    // Generate ID for new item
    item.id = Date.now().toString();
    
    // Add to local array
    this.learnMoreItems.push(item);

    // TODO: Here you would typically save to backend
    this.saveLearnMoreItem(item);
  }

  updateLearnMoreItem(updatedItem: LearnMore, index: number): void {
    // Update local array
    this.learnMoreItems[index] = updatedItem;

    // TODO: Here you would typically save to backend
    this.saveLearnMoreItem(updatedItem);
  }

  removeLearnMoreItem(index: number): void {
    // Remove from local array
    this.learnMoreItems.splice(index, 1);

    // TODO: Here you would typically delete from backend
  }

  onEditItem(event: { item: LearnMore; index: number }): void {
    this.openEditLearnMoreDialog(event.item, event.index);
  }

  onRemoveItem(event: { item: LearnMore; index: number }): void {
    this.removeLearnMoreItem(event.index);
  }

  private saveLearnMoreItem(item: LearnMore): void {
    // TODO: Implement actual save logic here
    console.log('Saving learn more item:', item);
  }
}